<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-sky-200 via-teal-200 to-emerald-200 overflow-hidden">
      <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      <div class="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl py-12 sm:py-16 lg:py-20">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div class="text-gray-800 lg:pr-12">
            <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              AI <span class="text-yellow-500"> 雙線 </span> 英語課程
            </h1>
            <p class="text-base sm:text-lg lg:text-xl mb-8 text-gray-700 leading-relaxed max-w-xl">
              專為中小學生設計的創新 AI 課程，結合多感官互動與豐富資源，全面提升英語能力與應試技巧。
            </p>
          </div>

          <!-- Right Image -->
          <div class="flex justify-center lg:justify-end">
            <div class="relative w-full max-w-xl"> <!-- 原本 max-w-lg 改成 max-w-xl -->
              <img src="@/assets/img/product5-0.jpg" alt="AI雙線少兒英語課程介紹圖"
                class="rounded-3xl shadow-2xl object-cover w-full h-auto" />
              <!-- Decorative Circles -->
              <div class="absolute -top-4 -right-4 w-20 h-20 bg-yellow-300 rounded-full opacity-20"></div>
              <div class="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-300 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
      </div>
    </section>

    <!-- 課程級別對照 -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-8 text-center">
          課程級別對照
        </h2>
        <div class="max-w-6xl mx-auto">

          <!-- 課程級別卡片 -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div class="bg-sky-50 rounded-lg p-6 text-center border-2 border-sky-200">
              <h3 class="text-xl font-bold text-sky-700 mb-2">初級課程</h3>
              <p class="text-sm text-sky-600 mb-2">Starter (S1~S4)</p>
              <p class="text-gray-700 mb-2">小學1-2年級學員</p>
              <p class="text-sm text-gray-600">中國標準國家一級能力等級</p>
            </div>

            <div class="bg-emerald-50 rounded-lg p-6 text-center border-2 border-emerald-200">
              <h3 class="text-xl font-bold text-emerald-700 mb-2">中級課程</h3>
              <p class="text-sm text-emerald-600 mb-2">Intermediate (I1~I8)</p>
              <p class="text-gray-700 mb-2">小學3-6年級學員</p>
              <p class="text-sm text-gray-600">中國標準國家二級能力等級</p>
            </div>

            <div class="bg-amber-50 rounded-lg p-6 text-center border-2 border-amber-200">
              <h3 class="text-xl font-bold text-amber-700 mb-2">高級課程</h3>
              <p class="text-sm text-amber-600 mb-2">Advanced (A1~A12)</p>
              <p class="text-gray-700 mb-2">小學4-6年級學員</p>
              <p class="text-sm text-gray-600">中國標準國家三級能力等級</p>
            </div>

            <div class="bg-purple-50 rounded-lg p-6 text-center border-2 border-purple-200">
              <h3 class="text-xl font-bold text-purple-700 mb-2">初中課程</h3>
              <p class="text-sm text-purple-600 mb-2">Junior (J1~J3)</p>
              <p class="text-gray-700 mb-2">初中7-9年級學員</p>
              <p class="text-sm text-gray-600">中國標準國家三級能力等級</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 特色功能區 -->
    <section class="py-12 bg-gray-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          特色功能
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <!-- 特色功能項目 1 -->
          <div class="flex flex-col items-center">
            <!-- 圖片 -->
            <div class="mb-6 cursor-pointer transform hover:scale-105 transition-transform duration-300"
              @click="showImg(0)">
              <img src="@/assets/img/product5-2.jpg" alt="AI智適應個性題庫"
                class="w-40 h-40 rounded-xl object-cover shadow-lg hover:shadow-xl transition-shadow duration-300" />
            </div>
            <!-- 卡片 -->
            <div
              class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full text-center">
              <h3 class="text-xl font-semibold text-gray-800 mb-3">AI智適應個性題庫</h3>
              <p class="text-gray-600">精準掃描知識漏洞，有效對接應試考試</p>
            </div>
          </div>

          <!-- 特色功能項目 2 -->
          <div class="flex flex-col items-center">
            <!-- 圖片 -->
            <div class="mb-6 cursor-pointer transform hover:scale-105 transition-transform duration-300"
              @click="showImg(1)">
              <img src="@/assets/img/product5-8.jpg" alt="AI單詞智學系統"
                class="w-40 h-40 rounded-xl object-cover shadow-lg hover:shadow-xl transition-shadow duration-300" />
            </div>
            <!-- 卡片 -->
            <div
              class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full text-center">
              <h3 class="text-xl font-semibold text-gray-800 mb-3">AI單詞智學系統</h3>
              <p class="text-gray-600">智能語音測評、反覆鞏固檢測</p>
            </div>
          </div>

          <!-- 特色功能項目 3 -->
          <div class="flex flex-col items-center">
            <!-- 圖片 -->
            <div class="mb-6 cursor-pointer transform hover:scale-105 transition-transform duration-300"
              @click="showImg(2)">
              <img src="@/assets/img/product5-3.jpg" alt="真人外教口語陪練"
                class="w-40 h-40 rounded-xl object-cover shadow-lg hover:shadow-xl transition-shadow duration-300" />
            </div>
            <!-- 卡片 -->
            <div
              class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full text-center">
              <h3 class="text-xl font-semibold text-gray-800 mb-3">真人外教口語陪練</h3>
              <p class="text-gray-600">一對一真人外教，25分鐘全程陪伴練習</p>
            </div>
          </div>

          <!-- 特色功能項目 4 -->
          <div class="flex flex-col items-center">
            <!-- 圖片 -->
            <div class="mb-6 cursor-pointer transform hover:scale-105 transition-transform duration-300"
              @click="showImg(3)">
              <img src="@/assets/img/product5-4.jpg" alt="趣味互動課件"
                class="w-40 h-40 rounded-xl object-cover shadow-lg hover:shadow-xl transition-shadow duration-300" />
            </div>
            <!-- 卡片 -->
            <div
              class="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 w-full text-center">
              <h3 class="text-xl font-semibold text-gray-800 mb-3">趣味互動課件</h3>
              <p class="text-gray-600">多元化、遊戲化、立體化，激發學習熱情</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Lightbox 組件 -->
    <vue-easy-lightbox :visible="lightboxVisible" :imgs="lightboxImages" :index="lightboxIndex" @hide="hideLightbox" />

    <!-- 上課方式 -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          上課方式
        </h2>
        <div class="max-w-4xl mx-auto">
          <div class="grid md:grid-cols-2 gap-12">

            <!-- 校區上課 -->
            <div class="text-center">
              <div class="bg-sky-100 rounded-full w-32 h-32 flex items-center justify-center mx-auto mb-6">
                <!-- 替代 icon：學校 -->
                <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-sky-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M12 3l9.5 5.5-9.5 5.5L2.5 8.5 12 3zM12 14v7m-6-2h12" />
                </svg>
              </div>
              <h3 class="text-2xl font-semibold text-gray-800 mb-3">校區上課</h3>
              <p class="text-gray-600">AI精品小班趣味互動課</p>
            </div>

            <!-- 家中上課 -->
            <div class="text-center">
              <div class="bg-emerald-100 rounded-full w-32 h-32 flex items-center justify-center mx-auto mb-6">
                <!-- 替代 icon：筆電/線上 -->
                <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-emerald-500" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M9.75 3h4.5a2.25 2.25 0 012.25 2.25v1.5M4.5 7.5v9.75a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V7.5H4.5z" />
                </svg>
              </div>
              <h3 class="text-2xl font-semibold text-gray-800 mb-3">家中上課</h3>
              <p class="text-gray-600">一對一外教口語陪練課</p>
            </div>

          </div>
        </div>
      </div>
    </section>


    <!-- 課程目標 -->
    <section class="py-12 bg-gray-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          課程目標
        </h2>

        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">

          <!-- 一級目標 -->
          <div class="bg-white rounded-lg p-6 shadow-lg">
            <h3 class="text-xl font-bold text-sky-700 mb-6 text-center">一級目標</h3>
            <div class="space-y-4">
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🎧 聽</h4>
                <p class="text-sm text-gray-600 mt-1">
                  識別有關人、家庭及熟悉事物的詞彙、短語以及簡單指令。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🗣️ 說</h4>
                <p class="text-sm text-gray-600 mt-1">
                  表達個人喜好、介紹自己以及周圍熟悉的人或物。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">📖 讀</h4>
                <p class="text-sm text-gray-600 mt-1">
                  閱讀簡單材料，如兒歌、童謠、簡短繪本和小故事等。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">✍️ 寫</h4>
                <p class="text-sm text-gray-600 mt-1">
                  正確書寫字母和常見的簡單核心字彙。
                </p>
              </div>
            </div>
          </div>

          <!-- 二級目標 -->
          <div class="bg-white rounded-lg p-6 shadow-lg">
            <h3 class="text-xl font-bold text-emerald-700 mb-6 text-center">二級目標</h3>
            <div class="space-y-4">
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🎧 聽</h4>
                <p class="text-sm text-gray-600 mt-1">
                  通過故事、日常對話等載體，獲取相關文章基本訊息。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🗣️ 說</h4>
                <p class="text-sm text-gray-600 mt-1">
                  根據圖片或提示詞描述人或物的主要特徵，敘述小故事內容。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">📖 讀</h4>
                <p class="text-sm text-gray-600 mt-1">
                  閱讀語言簡單、話題熟悉的簡短材料，理解其主要內容。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">✍️ 寫</h4>
                <p class="text-sm text-gray-600 mt-1">
                  掌握基本的詞語、語法句式和時態的運用。
                </p>
              </div>
            </div>
          </div>

          <!-- 三級目標 -->
          <div class="bg-white rounded-lg p-6 shadow-lg">
            <h3 class="text-xl font-bold text-amber-700 mb-6 text-center">三級目標</h3>
            <div class="space-y-4">
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🎧 聽</h4>
                <p class="text-sm text-gray-600 mt-1">
                  聽懂簡短口頭表達、收聽或觀看話題熟悉的廣播應試節目。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">🗣️ 說</h4>
                <p class="text-sm text-gray-600 mt-1">
                  能夠就熟悉話題與他人進行簡單交流，參與小組話題討論。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">📖 讀</h4>
                <p class="text-sm text-gray-600 mt-1">
                  閱讀簡單的應用文、議論文或簡短材料，能夠歸納要點，理解隱晦含意。
                </p>
              </div>
              <div class="border-t pt-2">
                <h4 class="font-semibold text-gray-800">✍️ 寫</h4>
                <p class="text-sm text-gray-600 mt-1">
                  正確選用基本句式結構和時態描述事物和事件，表達個人意願或態度。
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>


    <!-- 課程設置 -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-8 text-center">
          課程設置
        </h2>
        <div class="max-w-4xl mx-auto">
          <!-- 課程週期表格 -->
          <div class="bg-white rounded-lg shadow-lg overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-200 + text-gray-800">
                  <tr class="text-center">
                    <th class="py-4 px-6">課程週期</th>
                    <th class="py-4 px-6">課堂形式</th>
                    <th class="py-4 px-6">課時設置</th>
                    <th class="py-4 px-6">課時費用</th>
                    <th class="py-4 px-6">課程費用</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-b hover:bg-sky-50 transition">
                    <td class="py-4 px-6 font-medium" rowspan="2">
                      3個月/期<br>
                      <span class="text-sm text-gray-600">(共36節)</span>
                    </td>
                    <td class="py-4 px-6">AI精品小班趣味互動課</td>
                    <td class="py-4 px-6">24節課</td>
                    <td class="py-4 px-6">
                      <span class="line-through text-gray-500">原價1920元</span><br>
                      <span class="text-red-600 font-semibold">(7.5折) 現價1440元</span>
                    </td>
                    <td class="py-4 px-6 font-medium" rowspan="2">
                      2160元/期<br>
                      <span class="text-sm text-gray-600">(不含教材費)</span>
                    </td>
                  </tr>
                  <tr class="border-b hover:bg-sky-50 transition">
                    <td class="py-4 px-6">一對一外教口語陪練課</td>
                    <td class="py-4 px-6">12節課</td>
                    <td class="py-4 px-6">720元</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 教材費用 -->
          <div class="mt-6 text-center">
            <div class="inline-block bg-yellow-100 rounded-lg px-6 py-3">
              <span class="text-lg font-semibold text-gray-800">教材費：199元/期</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-12 bg-gray-50 -mb-3">
      <div class="container mx-auto px-6 text-center">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl font-bold text-gray-800 mb-6">
            立即體驗AI雙線少兒英語課程
          </h2>
          <p class="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            專業團隊提供完整的產品諮詢與技術支援，協助您快速部署
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link to="/contactus">
              <button aria-label="聯絡我們瞭解更多"
                class="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                聯絡我們瞭解更多
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
  import { ref } from 'vue'
  import VueEasyLightbox from 'vue-easy-lightbox'

  import img1 from '@/assets/img/product5-2.jpg'
  import img2 from '@/assets/img/product5-8.jpg'
  import img3 from '@/assets/img/product5-3.jpg'
  import img4 from '@/assets/img/product5-4.jpg'

  const lightboxVisible = ref(false)
  const lightboxIndex = ref(0)
  const lightboxImages = ref([img1, img2, img3, img4])

  const showImg = (index) => {
    lightboxIndex.value = index
    lightboxVisible.value = true
  }

  const hideLightbox = () => {
    lightboxVisible.value = false
  }
</script>

<style lang="scss" scoped>
  .text-gray-800 {
    position: relative;
    z-index: 1;
  }

  .absolute.inset-0 {
    z-index: 0;
  }

  .absolute.top-0.right-0 {
    z-index: 0;
  }

  section.bg-white {
    position: relative;
    z-index: 1;
  }

  .group:hover .w-6.h-6 {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    .grid.md\\:grid-cols-2 {
      grid-template-columns: 1fr;
    }

    .grid.md\\:grid-cols-3 {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .text-3xl.sm\\:text-4xl.lg\\:text-6xl {
      font-size: 2rem;
    }

    .text-base.sm\\:text-lg.lg\\:text-xl {
      font-size: 1rem;
    }

    table {
      font-size: 0.875rem;
    }

    th,
    td {
      padding: 0.75rem 0.5rem;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease-out;
  }

  button {
    transition: all 0.3s ease;
  }

  button:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    .flex.flex-col.sm\\:flex-row {
      flex-direction: column;
    }

    button {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  }

  img {
    transition: transform 0.3s ease;
  }

  img:hover {
    transform: scale(1.02);
  }

  .bg-white.rounded-xl {
    transition: all 0.3s ease;
  }

  .bg-white.rounded-xl:hover {
    transform: translateY(-4px);
  }

  .bg-gradient-to-br.from-sky-50.to-emerald-50 {
    background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
  }

  tbody tr:hover {
    background-color: rgba(14, 165, 233, 0.05);
  }

  .bg-sky-50,
  .bg-emerald-50,
  .bg-amber-50,
  .bg-purple-50 {
    transition: all 0.3s ease;
  }

  .bg-sky-50:hover,
  .bg-emerald-50:hover,
  .bg-amber-50:hover,
  .bg-purple-50:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }

  /* 特色功能區的額外樣式 */
  .cursor-pointer {
    cursor: pointer;
  }

  .transform {
    transition: transform 0.3s ease;
  }

  .hover\:scale-105:hover {
    transform: scale(1.05);
  }

  .shadow-lg {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .hover\:shadow-xl:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
</style>